import {Card, CardBody, CardHeader, Checkbox, CheckboxGroup} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import React, {useMemo, useState} from "react";
import {apiRetrieveImproved} from "../../../api.ts";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";
import {useJwtUser} from "../../../JWTAuthProvider.tsx";
import services from "../../../services";
import {AiInsightData, AiInsight} from "../../../types";
import useEntityEditor from "../../../user/_components/useEntityEditor.tsx";
import queryKeys from "../../../utilities/queryKeys.ts";
import {AiInsightResultStatus} from "../../../constants.ts";
import {AiInsightRenderer} from "../../../leagues/AiInsightRenderers";

export const Route = createFileRoute('/_app/ai/$ai_uid')({
    component:  RouteComponent,
    staticData: {
        backUrl: '/ai',
    }
})

function RouteComponent() {
    const user = useJwtUser();
    const queryClient = useQueryClient();
    const params = useParams({strict: false});
    const {entity: aiInsightValues, setEntity: aiInsightValuesSet, entityEditor: aiInsightEditor} = useEntityEditor<AiInsight>({
        meta: {}
    });
    const {entity: playgroundValues, setEntity: playgroundValuesSet, entityEditor: playgroundEditor} = useEntityEditor<any>({});

    const [response, setResponse] = useState('');
    const [isPolling, setIsPolling] = useState(false);
    const [isJobRunning, setIsJobRunning] = useState(false);

    const aiInsightQuery = useQuery({
        queryKey: queryKeys.aiInsights(params.ai_uid),
        queryFn:  async () => services.aiInsights.show(params.ai_uid!),
    })
    const leaguesQuery = useQuery({
        queryKey: queryKeys.leagues(user?.uid),
        queryFn:  async () => apiRetrieveImproved('leagues-get'),
        enabled:  !!user?.uid,
    })
    const leaguesMap = useMemo(() => {
        if (!leaguesQuery.data) {
            return {};
        }
        const map = {};
        leaguesQuery.data.forEach((league: any) => {
            map[league.uid] = league.title;
        })
        return map;
    }, [leaguesQuery.data])

    const updateAiInsight = useMutation({
        mutationFn: async () => {
            if (!aiInsightQuery.data) {
                return;
            }

            aiInsightValues.version = (aiInsight.version || 0) + 1;

            await services.aiInsights.update({
                ...aiInsightValues,
                uid: aiInsightQuery.data.uid,
            });
        },
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.aiInsights(params.ai_uid!)});
        }
    })

    const resultQuery = useQuery({
        queryKey: queryKeys.aiInsightsTestResult(params.ai_uid!, aiInsightQuery.data?.last_test_league_uid),
        queryFn: async () => {
            if (!aiInsightQuery.data?.last_test_league_uid) return null;
            return await services.aiInsights.getResult(aiInsightQuery.data.last_test_league_uid, params.ai_uid!);
        },
        enabled: !!aiInsightQuery.data?.last_test_league_uid,
        refetchInterval: isPolling ? 5000 : false,
    });

    React.useEffect(() => {
        const checkExistingResult = async () => {
            if (aiInsightQuery.data?.last_test_league_uid && !isPolling) {
                try {
                    const existingResult = await services.aiInsights.getResult(
                        aiInsightQuery.data.last_test_league_uid,
                        params.ai_uid!
                    );

                    if (existingResult) {
                        if (existingResult.status === AiInsightResultStatus.Processing || existingResult.status === AiInsightResultStatus.Pending) {
                            setIsPolling(true);
                            setIsJobRunning(true);
                            setResponse(`${existingResult.status === AiInsightResultStatus.Pending ? 'Analysis queued. Waiting to start...' : 'Processing...'}`);
                        }
                        else if (existingResult.status === AiInsightResultStatus.Completed) {
                            setResponse(existingResult.result || '[empty response]');
                            setIsJobRunning(false);
                        }
                        else if (existingResult.status === AiInsightResultStatus.Failed) {
                            setResponse(`Error: ${existingResult.error_message || 'Unknown error'}`);
                            setIsJobRunning(false);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching existing AI insight result:", error);
                }
            }
        };

        checkExistingResult();
    }, [aiInsightQuery.data?.last_test_league_uid, params.ai_uid]);

    const runAiInsight = useMutation({
        mutationFn: async (variables: any) => {
            console.log("variables", variables)
            return await services.aiInsights.test(variables.uid, variables.playgroundValues);
        },
        onSuccess:  () => {
            setIsPolling(true);
            setIsJobRunning(true);
            setResponse('Analysis started. Waiting for results...');
            resultQuery.refetch();
        },
        onError:    (result) => {
            setIsJobRunning(false);
            addToast({
                color: "danger",
                title: result.message || "Something went wrong.",
            })
        }
    })

    // Update response when result is available
    React.useEffect(() => {
        if (resultQuery.data) {
            const result = resultQuery.data;
            if (result.status === AiInsightResultStatus.Completed) {
                setResponse(result.result || '[empty response]');
                setIsPolling(false);
                setIsJobRunning(false);
            } else if (result.status === AiInsightResultStatus.Failed) {
                setResponse(`Error: ${result.error_message || 'Unknown error'}`);
                setIsPolling(false);
                setIsJobRunning(false);
            } else if (result.status === AiInsightResultStatus.Processing) {
                const elapsed = result.started_at ? Math.floor((Date.now() - new Date(result.started_at).getTime()) / 1000) : 0;
                setResponse(`Processing... (${elapsed}s elapsed)`);
                setIsJobRunning(true);
            } else if (result.status === AiInsightResultStatus.Pending) {
                setResponse('Analysis queued. Waiting to start...');
                setIsJobRunning(true);
            }
        }
    }, [resultQuery.data]);

    const aiInsight: AiInsight = {
        ...(aiInsightQuery.data || {}),
        ...aiInsightValues,
        meta: {
            ...(aiInsightQuery.data?.meta || {}),
            ...aiInsightValues.meta,
        },
    };

    if (aiInsightQuery.isLoading) {
        return <div className="flex justify-center my-6">
            <Spinner size={"lg"} />
        </div>
    }

    return <>
        <Header />
        <Content>
            <Card className={"mb-4"}>
                <CardHeader>
                    AI Insight
                </CardHeader>
                <CardBody className={"pb-4 grid gap-4"}>
                    {aiInsightEditor.input('title', 'AI Insight Title', aiInsight.title)}

                    {aiInsightEditor.select('Type', 'type', {
                        'league':     'League',
                        'tournament': 'Tournament',
                    }, aiInsight.type)}

                    {aiInsightEditor.select('Visibility', 'visibility_type', {
                        'public':  'Public',
                        'private': 'Private (only visible to admins)',
                        'hidden':  'Not visible',
                    }, aiInsight.visibility_type)}

                    {/*{aiInsightEditor.select('Model', 'model', {
                        'gpt-5':      'gpt-5',
                        'gpt-5-nano': 'gpt-5-nano',
                        'gpt-4o':     'gpt-4o',
                        'gpt-4.1':    'gpt-4.1',
                        'o4-mini':    'o4-mini',
                    }, aiInsight.model)}*/}

                    {aiInsightEditor.textarea("Prompt", 'prompt_intro', aiInsight.prompt_intro)}

                    <div className={"border rounded-lg p-4"}>
                        <div className={"mb-2"}>Tools available to the Agent:</div>
                        <ul>
                            <li>
                                <strong className={"font-mono"}>games_lookup</strong>: returns all games in the league (in ELO report format)
                            </li>
                        </ul>
                    </div>
                    {/*<CheckboxGroup
                        label="Select attachments (reducing the context might help AI reasoning)"
                        value={aiInsightValues.meta.attachments}
                        onValueChange={(arr) => {
                            aiInsightValuesSet({
                                ...aiInsightValues,
                                meta: {
                                    ...aiInsightValues.meta,
                                    attachments: arr,
                                }
                            })
                        }}
                    >
                        <Checkbox value="league_games">League games (ELO report CSV)</Checkbox>
                        <Checkbox value="all_games" isDisabled={true}>Complete ELO report (CSV)</Checkbox>
                        <Checkbox value="player_profiles">Full player profiles (CSV)</Checkbox>
                        <Checkbox value="global_players_overview">Overview of all players in the system (CSV)</Checkbox>
                    </CheckboxGroup>*/}

                    <CheckboxGroup
                        label="When should the insight be updated?"
                        value={aiInsight.meta.triggers}
                        onValueChange={(arr) => {
                            aiInsightValuesSet({
                                ...aiInsightValues,
                                meta: {
                                    ...aiInsightValues.meta,
                                    triggers: arr,
                                }
                            })
                        }}
                    >
                        <Checkbox value="league_game_finished">League game has finished</Checkbox>
                        <Checkbox value="night">At night</Checkbox>
                    </CheckboxGroup>

                    <PButton color={"primary"} isLoading={updateAiInsight.isPending} onPress={() => updateAiInsight.mutate()}>
                        Save
                    </PButton>

                </CardBody>
            </Card>

            <Card className={"mb-4"}>
                <CardHeader>
                    Testing
                </CardHeader>
                <CardBody className={"pb-4 grid gap-4"}>
                    {playgroundEditor.select('Run on League:', 'league_uid', leaguesMap, aiInsight.last_test_league_uid)}

                    <PButton
                        onPress={() => {
                            runAiInsight.mutate({
                                uid:              params.ai_uid,
                                playgroundValues: {
                                    league_uid: aiInsight.last_test_league_uid,
                                    ...playgroundValues,
                                }
                            })
                        }}
                        isLoading={runAiInsight.isPending}
                        isDisabled={isJobRunning || runAiInsight.isPending}
                    >
                        {isJobRunning ? 'Running...' : 'Run'}
                    </PButton>
                    <Card>
                        <CardHeader>Response:</CardHeader>
                        <CardBody>
                            {(() => {
                                const rawResponse = response || aiInsight.last_test_response;
                                if (!rawResponse) {
                                    return <div className="text-default-500">No response yet. Click "Run" to test this insight.</div>;
                                }

                                if (rawResponse.startsWith('Processing') ||
                                    rawResponse.startsWith('Analysis') ||
                                    rawResponse.startsWith('Error:') ||
                                    rawResponse === '[empty response]') {
                                    return <div className="whitespace-pre-wrap">{rawResponse}</div>;
                                }

                                try {
                                    const parsedData: AiInsightData = JSON.parse(rawResponse);
                                    return <AiInsightRenderer data={parsedData} />;
                                } catch {
                                    return (
                                        <div>
                                            <div className="text-xs text-warning mb-2">
                                                Legacy/Raw Format (update prompt to return JSON)
                                            </div>
                                            <div className="whitespace-pre-wrap text-xs font-mono bg-default-100 p-3 rounded">
                                                {rawResponse}
                                            </div>
                                        </div>
                                    );
                                }
                            })()}
                        </CardBody>
                    </Card>
                </CardBody>
            </Card>

        </Content>
    </>
}
